import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { setLedgerStatusAction } from "../../../../actions/setLedgerStatusAction";

/**
 * LedgerStatusModal
 * Same UI pattern as the ParentCode active/inactive modal.
 * Toggle: "Required" (yes) / "Not Required" (no)
 * Seeds from entity.isLedger — "yes" = required, "no" = not required
 */
const LedgerStatusModal = ({ isOpen, onClose, entityRow, onSuccess }) => {
  const entity = entityRow?.entity || {};
  const entityId = entity.entityId || "";
  const entityName = entity.entityName || entity.EntityName || "—";

  // Seed toggle from current API value when modal opens
  const [required, setRequired] = useState(entity.isLedger === "yes");
  const [submitting, setSubmitting] = useState(false);

  // Re-seed whenever the modal opens with a new entity
  useEffect(() => {
    if (isOpen) {
      setRequired((entityRow?.entity?.isLedger ?? "no") === "yes");
    }
  }, [isOpen, entityRow]);

  const handleClose = () => {
    setRequired((entityRow?.entity?.isLedger ?? "no") === "yes");
    onClose();
  };

  const handleSubmit = async () => {
    if (!entityId) {
      toast.error("Entity ID not found");
      return;
    }
    setSubmitting(true);
    const result = await setLedgerStatusAction(
      entityId,
      required ? "yes" : "no",
    );
    setSubmitting(false);

    if (result.success) {
      toast.success(result.message || "Ledger status updated successfully");
      if (onSuccess) onSuccess();
      handleClose();
    } else {
      toast.error(result.message || "Failed to update ledger status");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Ledger Requirement"
      size="small"
    >
      <div className="pc-status-modal">
        {/* Entity info strip — same as ParentCode modal */}
        <div className="pc-status-modal__info">
          <div className="pc-status-modal__avatar">
            {(entityName || "?")[0].toUpperCase()}
          </div>
          <div className="pc-status-modal__info-text">
            <p className="pc-status-modal__entity-name">{entityName}</p>
            <p className="pc-status-modal__entity-id">{entityId}</p>
          </div>
        </div>

        <div className="pc-status-modal__divider" />

        {/* Toggle section */}
        <div className="pc-status-modal__section">
          <p className="pc-status-modal__section-label">Ledger Required?</p>
          <p className="pc-status-modal__section-hint">
            Toggle to set whether a ledger is required for this entity.
          </p>

          <div className="pc-status-modal__toggle-wrap">
            <button
              className={`pc-status-option ${!required ? "pc-status-option--inactive" : ""}`}
              onClick={() => setRequired(false)}
            >
              <span className="pc-status-option__dot" />
              Not Required
            </button>

            <button
              className={`pc-toggle ${required ? "pc-toggle--on" : "pc-toggle--off"}`}
              onClick={() => setRequired((v) => !v)}
              aria-label="Toggle ledger requirement"
            >
              <span className="pc-toggle__thumb" />
            </button>

            <button
              className={`pc-status-option ${required ? "pc-status-option--active" : ""}`}
              onClick={() => setRequired(true)}
            >
              <span className="pc-status-option__dot" />
              Required
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="pc-status-modal__actions">
          <button
            className="pc-status-modal__btn-cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="pc-status-modal__btn-confirm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <span className="pc-status-modal__spinner" />}
            {submitting ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LedgerStatusModal;
